<script lang="ts">
  // Portrait do jogador — avatar webp + active ring + COUNTDOWN RING
  // que drena enquanto o tempo do turno passa (regra do Leonardo:
  // "o tempo circulando o player é dahora").
  //
  // O countdown ring usa SVG stroke-dasharray. Quando active=true
  // E deadline existe, anima do cheio ao vazio durante
  // turn_duration_sec.
  import { onDestroy } from 'svelte';
  import { playerSpeech } from '../../stores/speech';

  type Props = {
    name: string;
    active?: boolean;
    side: 'me' | 'opp';
    avatarKey?: string;
    deadline?: string | null;
    turnDurationSec?: number;
    playerId?: string;
    // RankingScore do jogador (snapshot.ranking_score) — badge dourado
    // embaixo do nick. Leonardo 2026-06-11: "aparecer o ranking do cara
    // dentro do game". null/0 = nao renderiza (snap antigo/restaurado).
    ranking?: number | null;
  };
  let {
    name,
    active = false,
    side,
    avatarKey,
    deadline = null,
    turnDurationSec = 30,
    playerId,
    ranking = null,
  }: Props = $props();

  // Balao de fala (truco/aceito/correu). Reactive: quando o speech
  // store muda, o balao aparece/some.
  let speech = $derived(playerId ? $playerSpeech.get(playerId) : undefined);

  const PORTRAIT_R = 32;
  const CIRC = 2 * Math.PI * PORTRAIT_R; // ~201

  let now = $state(Date.now());
  let tickId: number | null = null;

  // Tick a cada 100ms enquanto o ring esta ativo (deadline + active).
  $effect(() => {
    const needTick = active && !!deadline;
    if (needTick && tickId === null) {
      tickId = window.setInterval(() => (now = Date.now()), 100);
    }
    if (!needTick && tickId !== null) {
      clearInterval(tickId);
      tickId = null;
    }
  });
  onDestroy(() => {
    if (tickId !== null) clearInterval(tickId);
  });

  // Timer SINCRONIZADO com backend via tempo RELATIVO ao snap arrival
  // (nao ao ISO absoluto — evita clock skew client-vs-server). Quando
  // deadline muda no snap, registra tempo local e conta turnDurationSec
  // a partir dali. Felipe 2026-05-21: "timer tem que sincronizar com
  // o backend".
  let lastDeadlineKey = $state('');
  let timerStartedAt = $state(0);
  $effect(() => {
    const k = deadline ?? '';
    if (k !== lastDeadlineKey) {
      lastDeadlineKey = k;
      timerStartedAt = Date.now();
    }
  });
  let remainingSec = $derived(
    deadline && timerStartedAt
      ? Math.max(0, turnDurationSec - (now - timerStartedAt) / 1000)
      : 0,
  );
  let fraction = $derived(
    turnDurationSec > 0 ? Math.min(1, remainingSec / turnDurationSec) : 0,
  );
  // dashoffset 0 = cheio, CIRC = vazio
  let dashOffset = $derived(CIRC * (1 - fraction));
  let danger = $derived(remainingSec > 0 && remainingSec <= 5);

  let initial = $derived(name?.[0]?.toUpperCase() || '?');
  let imgSrc = $derived(avatarKey ? `/images/${avatarKey}.webp` : null);
</script>

<div class="portrait-wrap" class:active>
  {#if speech}
    {#key speech.key}
      <div class="speech tone-{speech.tone}" role="status">
        <span class="speech-text">{speech.text}</span>
      </div>
    {/key}
  {/if}
  <div class="portrait-stack">
    <div class="portrait" class:opp={side === 'opp'}>
      {#if imgSrc}
        <img src={imgSrc} alt={name} draggable="false" />
      {:else}
        <span class="initial">{initial}</span>
      {/if}
    </div>

    {#if active}
      <!-- Ring fora do .portrait (que tem overflow:hidden pra clipar
           o avatar). Posicionado absoluto no .portrait-stack pra
           ficar circulando por fora. -->
      <svg class="ring" class:indeterminate={!deadline} viewBox="0 0 80 80" aria-hidden="true">
        <circle
          class="track"
          cx="40" cy="40" r="36"
          fill="none" stroke-width="3"
        />
        <circle
          class="progress"
          class:danger={!!deadline && danger}
          cx="40" cy="40" r="36"
          fill="none" stroke-width="3"
          stroke-dasharray={deadline ? `${2 * Math.PI * 36}` : `${2 * Math.PI * 36 * 0.25} ${2 * Math.PI * 36 * 0.75}`}
          stroke-dashoffset={deadline ? (2 * Math.PI * 36) * (1 - fraction) : 0}
          transform="rotate(-90 40 40)"
          stroke-linecap="round"
        />
      </svg>
    {/if}
  </div>
  <div class="name" title={name}>{name || '—'}</div>
  {#if ranking != null && ranking > 0}
    <div class="rank" title="ranking">
      <svg class="rank-trophy" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M6 4h12v4a6 6 0 0 1-12 0V4z" stroke="currentColor" stroke-width="2" fill="currentColor"/>
        <path d="M6 6H4a2 2 0 0 0 0 4h2M18 6h2a2 2 0 0 1 0 4h-2" stroke="currentColor" stroke-width="2"/>
        <path d="M10 14h4v3h2v2H8v-2h2v-3z" fill="currentColor"/>
      </svg>
      {ranking}
    </div>
  {/if}
  {#if active && deadline && remainingSec > 0}
    <div class="secs" class:danger>{Math.ceil(remainingSec)}s</div>
  {/if}
</div>

<style>
  .portrait-wrap {
    position: relative;
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    min-width: 64px;
  }

  /* Balao de fala flutuante acima do portrait. Cauda apontando pra
   * cabeca do player. Auto-some apos ~2s (controle no speech store).
   * tone-shout = pedido truco (roxo), ack = aceito (verde), run = correu (vermelho). */
  .speech {
    position: absolute;
    bottom: calc(100% + 4px);
    left: 50%;
    transform: translateX(-50%);
    padding: 4px 12px;
    border-radius: 14px;
    font-weight: 900;
    font-size: 13px;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    white-space: nowrap;
    pointer-events: none;
    z-index: 6;
    animation: bubble-pop 0.22s cubic-bezier(0.2, 1.4, 0.4, 1);
  }
  .speech::after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 50%;
    transform: translateX(-50%) rotate(45deg);
    width: 10px;
    height: 10px;
    background: inherit;
    border-right: inherit;
    border-bottom: inherit;
    z-index: -1;
  }
  .speech.tone-shout {
    background: linear-gradient(135deg, #d850f0, #7a1d8e);
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.25);
    box-shadow: 0 6px 18px rgba(216, 80, 240, 0.45);
  }
  .speech.tone-ack {
    background: linear-gradient(135deg, #6cffbd, #1c8455);
    color: #001a10;
    border: 1px solid rgba(255, 255, 255, 0.25);
    box-shadow: 0 6px 18px rgba(108, 255, 189, 0.45);
  }
  .speech.tone-run {
    background: linear-gradient(135deg, #ff4c7e, #8a1838);
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.25);
    box-shadow: 0 6px 18px rgba(255, 76, 126, 0.45);
  }
  @keyframes bubble-pop {
    0%   { transform: translateX(-50%) translateY(8px) scale(0.5); opacity: 0; }
    60%  { transform: translateX(-50%) translateY(0)   scale(1.08); }
    100% { transform: translateX(-50%) translateY(0)   scale(1);   opacity: 1; }
  }
  .portrait {
    position: relative;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    background: linear-gradient(135deg, #2a1d4a, #1a0e2e);
    border: 2px solid var(--border);
    color: var(--text);
    font-size: 22px;
    font-weight: 800;
    overflow: hidden;
    transition: border-color 0.25s, box-shadow 0.25s, transform 0.25s;
  }
  .portrait.opp {
    background: linear-gradient(135deg, #4a1d2a, #2e0e1a);
  }
  .portrait img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
  }
  .portrait-wrap.active .portrait {
    border-color: var(--ok);
    box-shadow: 0 0 22px rgba(108, 255, 189, 0.55);
    transform: scale(1.04);
  }
  .initial { line-height: 1; user-select: none; }

  /* portrait-stack: container relativo que segura o portrait (com
   * overflow:hidden pra avatar) e o ring (absoluto, transbordando). */
  .portrait-stack {
    position: relative;
    width: 64px;
    height: 64px;
  }

  /* SVG ring por cima do portrait. FORA do .portrait (que tem
   * overflow:hidden pra clipar a imagem do avatar). Posicionado
   * no .portrait-stack pra circular ALEM da borda do portrait.
   * Quando ha deadline -> countdown drena. Sem deadline -> arc
   * gira (1.4s/volta) indicando estado "ativo sem prazo". */
  .ring {
    position: absolute;
    inset: -8px;
    width: calc(100% + 16px);
    height: calc(100% + 16px);
    pointer-events: none;
    z-index: 2;
  }
  .ring .track {
    stroke: rgba(108, 255, 189, 0.15);
  }
  .ring .progress {
    stroke: var(--ok);
    transition: stroke-dashoffset 0.18s linear, stroke 0.2s;
    filter: drop-shadow(0 0 6px rgba(108, 255, 189, 0.6));
  }
  .ring .progress.danger {
    stroke: var(--danger);
    filter: drop-shadow(0 0 8px rgba(255, 76, 126, 0.8));
  }
  /* Indeterminate mode: ring com gap parcial girando — feedback visual
   * pra "bot pensando" / "estado intermediario sem timer". */
  .ring.indeterminate {
    animation: ring-spin 1.4s linear infinite;
  }
  @keyframes ring-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  .name {
    font-size: 11px;
    color: var(--text-dim);
    letter-spacing: 0.5px;
    max-width: 80px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .portrait-wrap.active .name {
    color: var(--ok);
    font-weight: 600;
  }
  /* Badge de ranking — dourado discreto, mesmo trofeu do header do lobby */
  .rank {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 9px;
    font-weight: 800;
    color: var(--warn);
    font-family: ui-monospace, monospace;
    letter-spacing: 0.5px;
    line-height: 1;
  }
  .rank-trophy { width: 10px; height: 10px; flex-shrink: 0; }
  .secs {
    font-family: ui-monospace, monospace;
    font-size: 10px;
    color: var(--ok);
    font-weight: 700;
    line-height: 1;
    margin-top: 2px;
  }
  .secs.danger {
    color: var(--danger);
    animation: pulse-text 0.8s ease-in-out infinite;
  }
  @keyframes pulse-text {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.15); }
  }
</style>
